import React, { useEffect, useState, useContext, useCallback } from "react";

import LoadingSpinner from "@/components/ui-kit/Loading/LoadingSpinner/LoadingSpinner";
import { TextElement } from "@/components/ui-kit/TextElement/TextElement";
import SearchContext from "@/context/SearchContext";
import { ContentfulProduct } from "@/types/common";
import { AlgoliaResult, LinkedItem, Categories } from "@/types/search";

import ArticleSearch from "./ArticleSearch";
import DefaultSearch from "./DefaultSearch";
import NoResults from "./NoResults";
import ProductSearch from "./ProductSearch";
import QuizCTA from "./QuizCTA";
import styles from "./SearchResults.module.scss";
import SuggestedSearch from "./SuggestedSearch";
import { matchAlgoliaToContentful } from "./utils";

interface SearchResultsProps {
  data: AlgoliaResult[];
  querySearch: string;
  searchedItem: ContentfulProduct | LinkedItem | null; // tightened
  setSearchItem: (item: ContentfulProduct | LinkedItem | null) => void;
  isBottom?: boolean;
}

const SearchResults: React.FC<SearchResultsProps> = ({
  data,
  querySearch,
  searchedItem,
  setSearchItem,
  isBottom = false,
}) => {
  // Use find() instead of filter()[0] to avoid undefined array destructuring
  const zendeskData = data.find(
    (zenData) =>
      zenData.index === (process.env.NEXT_PUBLIC_ALGOLIA_ZENDESK_INDEX_NAME ?? "Zendesk_production")
  );
  const products = data.find(
    (productData) => productData.index === process.env.NEXT_PUBLIC_ALGOLIA_PRODUCTS_INDEX_NAME
  );
  const pages = data.find(
    (pageData) => pageData.index === (process.env.NEXT_PUBLIC_ALGOLIA_PAGES_INDEX_NAME ?? "Pages")
  );

  const {
    searchClient,
    contentfulProducts,
    defaultFAQs,
    defaultFAQsLoading,
    resultSearchVisible,
    setResultSearchVisible,
    setResultsFound,
  } = useContext(SearchContext);

  const hasMatchedProductSearch = products?.hits?.length ?? 0;
  const hasMatchedZendeskSearch = zendeskData?.hits?.length ?? 0;
  const hasMatchedPagesSearch = pages?.hits?.length ?? 0;
  const resultCount = hasMatchedProductSearch + hasMatchedZendeskSearch + hasMatchedPagesSearch;
  const { product, article } = resultSearchVisible;

  const [isAlgoliaLoading, setIsLoadingAlgolia] = useState(true);
  const [categories, setCategories] = useState<Categories>({});

  const getDefaultCategoriesFromAlgolia = useCallback(async () => {
    if (!searchClient) return;

    try {
      setIsLoadingAlgolia(true);
      const { hits, queryID } = await searchClient.searchSingleIndex({
        indexName: process.env.NEXT_PUBLIC_ALGOLIA_PRODUCTS_INDEX_NAME ?? "Products",
        searchParams: {
          query: "",
          clickAnalytics: true,
        },
      });

      const matchedProducts: ContentfulProduct[] = matchAlgoliaToContentful(
        contentfulProducts,
        hits
      );

      const cats: Categories = {
        "Hair Growth Supplements": [],
        "Wellness Boosters": [],
        "Scalp Care": [],
      };

      matchedProducts.forEach((product) => {
        const productWithQueryID = {
          ...product,
          queryID,
        };
        if (productWithQueryID.isCore) {
          cats["Hair Growth Supplements"].push(productWithQueryID);
        }
        if (productWithQueryID.isBooster) {
          cats["Wellness Boosters"].push(productWithQueryID);
        }
        if (productWithQueryID.isScalp) {
          cats["Scalp Care"].push(productWithQueryID);
        }
      });

      setCategories(cats);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`Algolia API error multipleQueries ${error}`);
    } finally {
      setIsLoadingAlgolia(false);
    }

    setResultSearchVisible({ product: false, article: false });
  }, [contentfulProducts, searchClient, setResultSearchVisible]);

  useEffect(() => {
    getDefaultCategoriesFromAlgolia();
  }, [getDefaultCategoriesFromAlgolia]);

  const [defaultPages, setDefaultPages] = useState<LinkedItem[]>([]);

  useEffect(() => {
    setResultsFound(resultCount);
  }, [resultCount, setResultsFound]);

  useEffect(() => {
    if (pages && defaultPages.length === 0) {
      setDefaultPages(pages?.hits || []);
    }
  }, [pages, defaultPages]);

  const onSearchClicked = (
    event: React.MouseEvent,
    product: ContentfulProduct | LinkedItem | null
  ) => {
    setSearchItem(product);

    if (product && "imageId" in product && product.imageId) {
      setResultSearchVisible({ product: true, article: false });
      return;
    }

    setResultSearchVisible({ product: false, article: true });
  };

  if (defaultFAQsLoading || isAlgoliaLoading) {
    return (
      <div className="relative w-full h-full">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <>
      <div className={`${styles.searchResultsContainer} flex flex-col`}>
        {!product && !article && (
          <>
            {!querySearch.length ? (
              <DefaultSearch
                title="Popular Products"
                categories={categories}
                faq={defaultFAQs}
                onClick={onSearchClicked}
              />
            ) : !resultCount ? (
              <NoResults
                text={querySearch}
                faq={defaultFAQs}
                pages={defaultPages}
                category={categories["Hair Growth Supplements"] || []}
                onClick={onSearchClicked}
              />
            ) : (
              <SuggestedSearch
                products={products}
                articles={zendeskData?.hits}
                pages={pages?.hits}
                onClick={onSearchClicked}
              />
            )}
          </>
        )}

        {product ? (
          <ProductSearch
            data={searchedItem}
            onClick={onSearchClicked}
            queryID={products?.queryID}
          />
        ) : null}
        {article ? <ArticleSearch data={searchedItem} onClick={onSearchClicked} /> : null}

        {product ? (
          <div className={styles.disclaimer}>
            <div className={styles.footerBox}>
              <TextElement
                text="*These statements have not been evaluated by the Food and Drug Administration. This product is not intended to diagnose, treat, cure, or prevent any disease."
                element="p"
                className="body--small text-master-base-alt-disabled"
              />
            </div>
          </div>
        ) : null}
      </div>

      {!product && !article && <QuizCTA isMobile isBottom={isBottom} />}
    </>
  );
};

export default SearchResults;
